import React, { useState } from 'react';
import { User, Image, Phone, Shield, Upload, Camera, Trash2, KeyRound } from 'lucide-react';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import { uploadToStorage, getAuthToken } from '../../../config/supabase';
import { getAssignableModules } from '../../../config/modules';

export default function StaffForm({ employee, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const { add, update } = useCollection('staff');

  const [activeTab, setActiveTab] = useState('general');
  const [photoUploading, setPhotoUploading] = useState(false);

  // Employee login account (credentials handled securely server-side).
  const [account, setAccount] = useState(() => ({
    username: employee?.account?.username || '',
    password: '',
    status: employee?.account?.status || 'active',
    hasAccount: !!employee?.account?.hasAccount,
  }));
  const [accountBusy, setAccountBusy] = useState(false);

  const [data, setData] = useState(() => {
    if (employee) {
      return {
        ...employee,
        permissions: employee.permissions || {
          dashboard: true,
          clients: true,
          cars: true,
          rentals: true,
          finances: false,
          gps: false,
          admin: false
        },
        commissionPerRental: employee.commissionPerRental ?? 25
      };
    }
    return {
      name: '',
      role: '',
      phone: '',
      cin: '',
      address: '',
      email: '',
      hireDate: new Date().toISOString().split('T')[0],
      salaryType: 'monthly',
      salaryAmount: 0,
      paymentDay: 1,
      commissionPerRental: 25,
      status: 'active',
      notes: '',
      bankAccount: '',
      emergencyContact: '',
      photo: null,
      permissions: {
        dashboard: true,
        clients: true,
        cars: true,
        rentals: true,
        finances: false,
        gps: false,
        admin: false
      }
    };
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const url = await uploadToStorage(file, 'staff_photos');
      setData(prev => ({ ...prev, photo: url }));
    } catch (err) {
      alert('Error uploading photo: ' + err.message);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleTogglePermission = (permKey) => {
    setData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permKey]: !prev.permissions[permKey]
      }
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!data.name || !data.role) {
      return alert(isAr ? '⚠️ الاسم والوظيفة مطلوبة' : '⚠️ Nom et rôle requis');
    }
    
    const _comm = parseFloat(data.commissionPerRental);
    const payload = {
      ...data,
      salaryAmount: parseFloat(data.salaryAmount) || 0,
      paymentDay: parseInt(data.paymentDay) || 1,
      commissionPerRental: Number.isFinite(_comm) ? _comm : 25
    };
    
    try {
      let staffId = employee?.id;
      if (staffId) {
        await update(staffId, payload);
      } else {
        payload.createdAt = new Date().toISOString();
        const inserted = await add(payload);
        staffId = inserted?.id || inserted?._id || staffId;

        // Fetch currency setting if available
        const savedSettings = localStorage.getItem('appSettings');
        let currency = 'MAD';
        if (savedSettings) {
          try {
            currency = JSON.parse(savedSettings).currency || 'MAD';
          } catch (_) {}
        }

        await sendTelegramNotification(
          buildTelegramMessage('new_staff', {
            name: data.name, role: data.role, salaryAmount: data.salaryAmount,
            currency, salaryType: data.salaryType
          }),
          'new_staff'
        );
      }

      // Persist the employee login account (secure, admin-only endpoint).
      if (staffId && account.username.trim()) {
        try {
          const agencyId = (typeof window !== 'undefined' && window.__AGENCY_ID__) || null;
          const authToken = await getAuthToken();
          const r = await fetch('/api/staff/set-credentials', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
            body: JSON.stringify({
              staffId, agencyId,
              username: account.username.trim(),
              password: account.password || undefined,
              status: account.status,
            }),
          });
          const j = await r.json().catch(() => ({}));
          if (!r.ok || !j.ok) throw new Error(j.error || 'Échec de l\'enregistrement du compte');
          // Mirror non-sensitive meta so the form/list can display the account.
          await update(staffId, { account: { username: j.username, status: j.status, hasAccount: true } });
        } catch (accErr) {
          alert('⚠️ ' + (isAr ? 'الحساب: ' : 'Compte : ') + accErr.message);
        }
      }
      onClose();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const resetPassword = async () => {
    const np = window.prompt(isAr ? 'كلمة مرور جديدة (6 أحرف على الأقل):' : 'Nouveau mot de passe (min 6 caractères) :');
    if (!np) return;
    if (np.length < 6) return alert(isAr ? 'كلمة المرور قصيرة جداً' : 'Mot de passe trop court');
    if (!employee?.id) return;
    setAccountBusy(true);
    try {
      const authToken = await getAuthToken();
      const r = await fetch('/api/staff/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({ staffId: employee.id, newPassword: np }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'Échec');
      alert('✅ ' + (isAr ? 'تم إعادة تعيين كلمة المرور' : 'Mot de passe réinitialisé'));
    } catch (e) {
      alert('❌ ' + e.message);
    } finally {
      setAccountBusy(false);
    }
  };

  const roleOptions = [
    { fr: "Directeur d'agence", ar: "مدير الوكالة" },
    { fr: "Responsable de succursale", ar: "مدير الفرع" },
    { fr: "Agent de réservation", ar: "وكيل الحجوزات" },
    { fr: "Agent de location", ar: "وكيل الكراء" },
    { fr: "Chargé de clientèle", ar: "ممثل خدمة العملاء" },
    { fr: "Gestionnaire de flotte", ar: "مدير الأسطول" },
    { fr: "Inspecteur de véhicules", ar: "مفتش السيارات" },
    { fr: "Agent de livraison & reprise", ar: "وكيل تسليم واستلام السيارات" },
    { fr: "Chauffeur", ar: "سائق" },
    { fr: "Assistant administratif", ar: "مساعد إداري" },
    { fr: "Comptable", ar: "محاسب" },
    { fr: "Commercial / Ventes", ar: "وكيل التسويق والمبيعات" },
    { fr: "Directeur des opérations", ar: "مدير العمليات" },
    { fr: "Coordinateur de maintenance", ar: "منسق الصيانة" }
  ];

  const t = (fr, ar) => (isAr ? ar : fr);

  // Tabs for the Form
  const formTabs = [
    { id: 'general', label: t('Emploi & Contrat', 'العمل والراتب'), icon: User },
    { id: 'photo', label: t('Photo', 'الصورة'), icon: Image },
    { id: 'contact', label: t('Contacts & Urgence', 'التواصل والطوارئ'), icon: Phone },
    { id: 'permissions', label: t('Permissions', 'الصلاحيات'), icon: Shield },
    { id: 'account', label: t('Compte / Connexion', 'حساب الدخول'), icon: KeyRound }
  ];

  return (
    <Modal onClose={onClose} title={employee ? t('✏️ Modifier l\'employé', '✏️ تعديل موظف') : t('➕ Nouvel employé', '➕ موظف جديد')}>
      <form onSubmit={submit} className="space-y-6" dir={isAr ? 'rtl' : 'ltr'}>
        
        {/* Horizontal Form Tabs */}
        <div className="flex border-b border-gray-200 pb-2 overflow-x-auto gap-2 scrollbar-none">
          {formTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg whitespace-nowrap transition-all ${
                  isActive 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-650 hover:bg-gray-100 hover:text-gray-900 text-gray-600'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* TAB CONTENT */}
        
        {/* 1. GENERAL TAB */}
        {activeTab === 'general' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={'📋 ' + t('Nom complet', 'الاسم الكامل')}
                value={data.name}
                onChange={v => setData({ ...data, name: v })}
                placeholder="Ahmed Benali"
              />
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  {'💼 ' + t('Poste/Rôle', 'الوظيفة')}
                </label>
                <input
                  type="text"
                  value={data.role}
                  onChange={e => setData({ ...data, role: e.target.value })}
                  list="role-options"
                  placeholder={t('Ex: Chauffeur', 'مثلا: سائق')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500"
                />
                <datalist id="role-options">
                  {roleOptions.map((r, idx) => <option key={idx} value={isAr ? r.ar : r.fr} />)}
                </datalist>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={'📅 ' + t("Date d'embauche", 'تاريخ التوظيف')}
                type="date"
                value={data.hireDate}
                onChange={v => setData({ ...data, hireDate: v })}
              />
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  {'📊 ' + t('Statut', 'الحالة')}
                </label>
                <select
                  value={data.status}
                  onChange={e => setData({ ...data, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">✅ {t('Actif', 'نشط')}</option>
                  <option value="inactive">⛔ {t('Inactif', 'غير نشط')}</option>
                </select>
              </div>
            </div>

            {/* Salary section */}
            <div className="border-t border-gray-150 pt-3">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                💰 {t('Mode de rémunération', 'طريقة الاحتساب والراتب')}
              </label>
              <p className="text-[10px] text-gray-500 mb-2">
                {t('Choisissez UN seul mode de rémunération pour cet employé.',
                   'اختر طريقة أجر واحدة فقط لهذا الموظف.')}
              </p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { id: 'commission', emoji: '💵', fr: 'Commission', ar: 'عمولة', sub: t('Par contrat', 'لكل عقد') },
                  { id: 'monthly',    emoji: '📆', fr: 'Mensuel',    ar: 'شهري',  sub: t('Fixe/mois', 'ثابت/شهر') },
                  { id: 'daily',      emoji: '☀️', fr: 'Journalier', ar: 'يومي',  sub: t('Par jour', 'باليوم') },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setData({ ...data, salaryType: opt.id })}
                    className={`p-2.5 rounded-lg border-2 transition text-center ${
                      data.salaryType === opt.id
                        ? 'bg-amber-50 border-amber-500 shadow-sm'
                        : 'bg-white border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="text-lg mb-0.5">{opt.emoji}</div>
                    <div className="text-xs font-bold">{isAr ? opt.ar : opt.fr}</div>
                    <div className="text-[9px] text-gray-500">{opt.sub}</div>
                  </button>
                ))}
              </div>

              {data.salaryType === 'commission' ? (
                <div>
                  <Field
                    label={'💵 ' + t('Commission par contrat (MAD)', 'العمولة لكل عقد (درهم)')}
                    type="number"
                    value={data.commissionPerRental}
                    onChange={v => setData({ ...data, commissionPerRental: v })}
                    placeholder="25"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    {t('Ajoutée automatiquement à chaque contrat assigné à cet employé (défaut 25 MAD).',
                       'تُضاف تلقائياً لكل عقد يُسند لهذا الموظف (الافتراضي 25 درهم).')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    label={data.salaryType === 'monthly'
                      ? '💵 ' + t('Salaire mensuel', 'الراتب الشهري')
                      : '💵 ' + t('Salaire journalier', 'الراتب اليومي')}
                    type="number"
                    value={data.salaryAmount}
                    onChange={v => setData({ ...data, salaryAmount: v })}
                    placeholder={data.salaryType === 'monthly' ? '3000' : '150'}
                  />
                  {data.salaryType === 'monthly' && (
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        {'📅 ' + t('Jour de paie', 'يوم الصرف')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={data.paymentDay}
                        onChange={e => setData({ ...data, paymentDay: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="1-31"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bank detail */}
            <div className="pt-2">
              <Field
                label={'🏦 ' + t('Compte bancaire (RIB)', 'الحساب البنكي (RIB)')}
                value={data.bankAccount}
                onChange={v => setData({ ...data, bankAccount: v })}
                placeholder="24 chiffres..."
              />
            </div>
          </div>
        )}

        {/* 2. PHOTO TAB */}
        {activeTab === 'photo' && (
          <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <div className="relative mb-4">
              {data.photo ? (
                <img 
                  src={data.photo} 
                  alt="Profile Preview" 
                  className="w-32 h-32 rounded-full object-cover ring-4 ring-blue-500 shadow-md"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-blue-100 border border-blue-200 text-blue-500 flex items-center justify-center text-4xl shadow-inner">
                  👤
                </div>
              )}
              {data.photo && (
                <button
                  type="button"
                  onClick={() => setData(prev => ({ ...prev, photo: null }))}
                  className="absolute bottom-0 right-0 p-2 bg-red-100 hover:bg-red-200 border border-red-300 rounded-full text-red-600 transition shadow"
                  title="Supprimer la photo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="text-center space-y-2">
              <label 
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow cursor-pointer transition"
              >
                <Upload className="w-4 h-4" />
                <span>{photoUploading ? t('Téléversement...', 'جاري الرفع...') : t('Téléverser une photo', 'تحميل صورة')}</span>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handlePhotoUpload} 
                  className="hidden" 
                  disabled={photoUploading}
                />
              </label>
              <p className="text-[10px] text-gray-500">
                {t('Formats acceptés: PNG, JPG. Max 5Mo.', 'الصيغ المقبولة: PNG، JPG. الأقصى 5 ميجا.')}
              </p>
            </div>
          </div>
        )}

        {/* 3. CONTACT TAB */}
        {activeTab === 'contact' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={'📞 ' + t('Téléphone', 'الهاتف')}
                value={data.phone}
                onChange={v => setData({ ...data, phone: v })}
                placeholder="06 12 34 56 78"
              />
              <Field
                label={'✉️ ' + t('Email', 'البريد الإلكتروني')}
                type="email"
                value={data.email}
                onChange={v => setData({ ...data, email: v })}
                placeholder="contact@exemple.com"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="🪪 CIN"
                value={data.cin}
                onChange={v => setData({ ...data, cin: v })}
                placeholder="AB123456"
              />
              <Field
                label={'🏠 ' + t('Adresse', 'العنوان')}
                value={data.address}
                onChange={v => setData({ ...data, address: v })}
              />
            </div>

            <div className="border-t border-gray-150 pt-3">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                🆘 {t('Contact d\'urgence', 'جهة الاتصال للطوارئ')}
              </label>
              <Field
                label={t('Nom complet & relation & téléphone', 'الاسم والقرابة والهاتف')}
                value={data.emergencyContact}
                onChange={v => setData({ ...data, emergencyContact: v })}
                placeholder="Ex: Fatima Benali (Mère) - 06 11 22 33 44"
              />
            </div>

            <div className="border-t border-gray-150 pt-3">
              <label className="block text-sm font-bold text-gray-700 mb-1">
                {'📝 ' + t('Notes / Commentaires internes', 'ملاحظات إضافية')}
              </label>
              <textarea
                value={data.notes}
                onChange={e => setData({ ...data, notes: e.target.value })}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('Notes internes...', 'ملاحظات داخلية...')}
              />
            </div>
          </div>
        )}

        {/* 4. PERMISSIONS TAB */}
        {activeTab === 'permissions' && (
          <div className="space-y-3">
            <div className="p-3 bg-blue-50/50 border border-blue-150 rounded-xl mb-3">
              <p className="text-[10px] text-blue-800 leading-relaxed font-semibold">
                ⚠️ {t(
                  'Cochez les modules de l\'application que cet employé sera autorisé à voir et à gérer.',
                  'حدد الوحدات التي ترغب في السماح للموظف بالوصول إليها وإدارتها في التطبيق.'
                )}
              </p>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {getAssignableModules().map(mod => {
                const item = { key: mod.id, label: isAr ? mod.ar : mod.fr };
                const isChecked = !!data.permissions[item.key];
                return (
                  <div 
                    key={item.key} 
                    onClick={() => handleTogglePermission(item.key)}
                    className={`p-3 border rounded-xl flex items-center justify-between gap-4 cursor-pointer transition ${
                      isChecked 
                        ? 'bg-blue-50/30 border-blue-300 shadow-sm' 
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xs font-bold text-gray-900">{item.label}</span>
                    <div className={`w-9 h-5 rounded-full shrink-0 p-0.5 transition-colors ${isChecked ? 'bg-blue-600' : 'bg-gray-250 bg-gray-250 bg-gray-200'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${isChecked ? (isAr ? '-translate-x-4' : 'translate-x-4') : 'translate-x-0'}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 5. ACCOUNT / LOGIN TAB */}
        {activeTab === 'account' && (
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-150 rounded-xl text-[11px] text-amber-800 font-semibold leading-relaxed">
              🔐 {t(
                'Donnez à cet employé son propre identifiant de connexion. Le mot de passe est chiffré (bcrypt) et jamais stocké en clair. L\'employé n\'accèdera qu\'aux modules autorisés dans l\'onglet Permissions.',
                'امنح هذا الموظف اسم مستخدم وكلمة مرور خاصة به. كلمة المرور مشفّرة ولا تُحفظ كنص صريح. لن يصل الموظف إلا للوحدات المسموح بها في تبويب الصلاحيات.'
              )}
            </div>

            <Field
              label={'👤 ' + t("Nom d'utilisateur (unique)", 'اسم المستخدم (فريد)')}
              value={account.username}
              onChange={v => setAccount(a => ({ ...a, username: v.replace(/\s/g, '') }))}
              placeholder={t('ex: ahmed.b', 'مثال: ahmed.b')}
            />

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                {'🔑 ' + (account.hasAccount
                  ? t('Nouveau mot de passe (vide = inchangé)', 'كلمة مرور جديدة (فارغ = بدون تغيير)')
                  : t('Mot de passe', 'كلمة المرور'))}
              </label>
              <input
                type="password"
                value={account.password}
                onChange={e => setAccount(a => ({ ...a, password: e.target.value }))}
                placeholder={account.hasAccount ? '••••••' : t('min 6 caractères', '6 أحرف على الأقل')}
                autoComplete="new-password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                {'📊 ' + t('Statut du compte', 'حالة الحساب')}
              </label>
              <select
                value={account.status}
                onChange={e => setAccount(a => ({ ...a, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">✅ {t('Actif', 'نشط')}</option>
                <option value="inactive">⛔ {t('Inactif (connexion bloquée)', 'غير نشط (الدخول محظور)')}</option>
              </select>
            </div>

            {account.hasAccount && (
              <div className="pt-2 border-t border-gray-150">
                <button
                  type="button"
                  disabled={accountBusy}
                  onClick={resetPassword}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-bold hover:bg-red-100 transition disabled:opacity-50"
                >
                  <KeyRound className="w-4 h-4" />
                  {accountBusy ? '⏳ ...' : t('Réinitialiser le mot de passe', 'إعادة تعيين كلمة المرور')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Fixed Footer Buttons */}
        <div className="flex gap-2 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold transition text-gray-700 text-sm"
          >
            {t('Annuler', 'إلغاء')}
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition text-sm"
          >
            💾 {t('Enregistrer', 'حفظ')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
